import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, Form } from 'formik';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import ApplicationRecord from '../../../records/application';
import CTAButton from '../../shared/CTAButton';
import ColumnInput from '../../shared/ColumnInput';
import Error from '../../shared/Error';
import GA from '../../../lib/utils/ga';
import Heap from '../../../lib/utils/heap';
import Loading from '../../shared/Loading';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import UserRecord from '../../../records/user';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import useValidateRoute from '../../../hooks/useValidateRoute';
import { asyncActions, handleAsyncError } from '../../../actions/general';
import { daysAsMonths } from '../../../lib/utils/formatters';
import { getRoute, loading, updateSession } from '../../../actions/sessions';
import { setHeight } from '../../../lib/utils/modal';
import { validatePhone } from '../../../actions/users';
import {
  subDomains,
  suffixes,
  emailRegex,
} from '../../../lib/constants/SiteVariables';
import classNames from 'classnames';

const heap = new Heap('NewUser');
const ga = new GA();

export default function NewUser({ location }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const session = useSelector((state) => state.sessions.get('session'));
  const pi4Confirmed = useSelector((state) =>
    state.sessions.get('pi4_confirmed'),
  );
  const application = useSessionEntity('application');
  const shopping_cart = useSessionEntity('shopping_cart');
  const merchant = useSessionEntity('merchant');
  const sessionUser = useSessionEntity('user');
  const prequalifiedUser = useSelector((state) =>
    state.entities.get('prequalification').first(),
  );
  const asyncErrors = useSelector((state) => state.general.get('errors'));
  const [newUser, setNewUser] = useState(sessionUser);
  const [foundUser, setFoundUser] = useState(false);
  const [isRouting, setIsRouting] = useState(false);

  useRouteStatus(location);

  useEffect(() => {
    setNewUser(sessionUser);
    dispatch(loading(false));
  }, [sessionUser]);

  useEffect(() => {
    if (!sessionUser && !isLoading) {
      const u = new UserRecord(shopping_cart && shopping_cart.billing_address);
      setNewUser(u.set('phone_number', ''));
    }
  }, [isLoading]);

  useEffect(() => {
    if (merchant && merchant.isOnHold) history.push('/error');

    if (newUser && merchant) {
      const content = document.getElementById('content');
      content &&
        session.mode === 'modal' &&
        setHeight(content.clientHeight + 220);
    }
  }, [newUser, merchant]);

  useEffect(() => {
    if (foundUser) history.push('/init/existing_user');
  }, [foundUser]);

  useEffect(() => {
    if (
      asyncErrors.count() > 0 &&
      asyncErrors.find((e) => e && e.includes('User already registered'))
    ) {
      setFoundUser(true);
      dispatch(loading(false));
    }
  }, [asyncErrors]);

  const checkPhone = async (val) => {
    const res = await dispatch(validatePhone(val));
    return res.result;
  };

  const checkEmail = (val) => {
    const tld = val.substr(val.lastIndexOf('.') + 1);
    if (
      [
        'con',
        'cok',
        'fom',
        'comm',
        'coml',
        'comt',
        'ccom',
        'ney',
        'edi',
        'cpm',
        'comq',
        'com2',
      ].includes(tld)
    )
      return false;

    return true;
  };

  const save = (values) => {
    setIsRouting(true);
    dispatch(loading(true));

    ga.identify({
      id: newUser.id,
      sessionId: session.id,
      sessionKey: session.session_key,
      merchantId: merchant.id,
      publicKey: session.public_key,
    });

    heap.track({
      merchant: merchant,
      session,
      user: newUser,
      payload: { amount: shopping_cart ? shopping_cart.grand_total : 0 },
    });

    // set phone for business to be same as initial primary mobile reported by user
    values.business_phone = values.phone_number;

    return dispatch(
      asyncActions(
        updateSession(
          {
            ...session.setCustomFlag(subDomains.subDomain()).toJS(),
            user: new UserRecord({ ...values }),
            application: new ApplicationRecord({
              ...values,
              session_id: session.id,
            }).toJS(),
          },
          { cart: false, prequal: false, merchant: false },
        ),
        getRoute(),
      ),
    ).then((res) => {
      dispatch(loading(false));
      setIsRouting(false);
    });
  };

  const isRecharge = session && session.id && (session.hasCustomFlag('recharge') || session.hasCustomFlag('instore'));

  const StandardHeader = () => {
    return (
      <div className="mt-4 mb-2">
        <div className="text-xl text-navy-blue leading-8 font-avenir-medium">
          {session.hasCustomFlag('netx')
            ? 'Easy terms for business purchasers'
            : 'Easy monthly terms for business purchases'}
        </div>

        {!session.hasCustomFlag('netx') && (
          <>
            {merchant.active_promo.get('max_net_payment') > 30 ? (
              <>
                <div className="mt-3 text-positive-green text-3xl font-avenir-black">
                  No payments for {merchant.active_promo.get('max_net_payment')}{' '}
                  days
                </div>
                <strong>No early pay-off fees.</strong>
              </>
            ) : (
              <div className="mt-3 text-positive-green text-2xl 3xs:text-3xl font-avenir-black">
                0% for{' '}
                {daysAsMonths(merchant.active_promo.get('max_net_duration'))}
              </div>
            )}
            <div className="my-3 text-sm 3xs:text-base">
              Enter a few details and get a decision in seconds.
            </div>
          </>
        )}
      </div>
    );
  };

  const RechargeHeader = () => {
    return (
      <div className="text-xl text-navy-blue leading-8 font-avenir-black">
        Let's start with the basics
      </div>
    );
  };

  if (!useValidateRoute({ company: true })) return <></>;

  if (newUser && newUser.account_id && !pi4Confirmed)
    return <Redirect to="/init/conversion" />;

  if (!newUser || !merchant) {
    return <Loading containerClass="mt-12" />;
  }

  if (newUser && merchant) {
    return (
      <div role="main" id="content" className="text-center mx-6 sm:mx-auto">
        {prequalifiedUser && (
          <div
            role="prequalification"
            className="text-xl text-navy-blue leading-7 font-avenir-black"
          >
            Welcome, {prequalifiedUser?.owner_first_name}
            <br />
            We look forward to working with you.
          </div>
        )}

        {isRecharge ? RechargeHeader() : StandardHeader()}

        <div className="font-avenir-medium text-dark-gray text-xs 3xs:text-sm">
          {session.hasCustomFlag('netx')
            ? 'Provide a few details to create a Credit Key account.'
            : `This is a business ${isRecharge ? 'application' : 'loan'}.`}
        </div>
        {!session.hasCustomFlag('netx') && (
          <div className="mb-5 font-avenir-medium text-dark-gray text-xs 3xs:text-sm">
            Applying will <u>not</u> affect your personal credit.
          </div>
        )}

        <Error />

        {!isRecharge && !session.hasCustomFlag('netx') && (
          <div className="isolate my-4 lg:mx-0 lg:max-w-none">
            <div className="ring-gray-200 rounded-xl py-3 px-1 3xs:p-3 mobile:p-5 ring-1 font-avenir-book">
              <h3 className="text-lg font-avenir-black text-navy-blue leading-8">
                Requirements:
              </h3>
              <ul
                role="list"
                className="mt-2 mobile:mt-4 space-y-1.5 text-sm mobile:text-base leading-2 3xs:leading-6"
              >
                <li className="flex gap-x-2 3xs:gap-x-3">
                  <FontAwesomeIcon
                    icon="check"
                    className="text-positive-green ml-4 3xs:mt-1"
                  />
                  600+ FICO
                </li>
                <li className="flex gap-x-2 3xs:gap-x-3 text-left 3xs:text-center">
                  <FontAwesomeIcon
                    icon="check"
                    className="text-positive-green ml-4 3xs:mt-1"
                  />
                  A valid, operating business
                </li>
                <li className="flex gap-x-2 3xs:gap-x-3 text-left 3xs:text-center">
                  <FontAwesomeIcon
                    icon="check"
                    className="text-positive-green ml-4 3xs:mt-1"
                  />
                  $40k+ gross annual revenue
                </li>
              </ul>
            </div>
          </div>
        )}

        <Formik
          initialValues={{
            ...newUser.toJS(),
            dba: (application && application.dba) || '',
          }}
          enableReinitialize={true}
          validate={(values) => {
            const errors = {};
            if (!values.first_name) errors.first_name = '* Required';
            if (!values.last_name) errors.last_name = '* Required';

            if (!values.phone_number) {
              errors.phone_number = '* Required';
            } else if (values.phone_number.replace(/[^\d]/g, '').length < 10) {
              errors.phone_number = '* Please enter a valid phone number';
            }

            if (!values.company_name) {
              errors.company_name = '* Required';
            } else if (values.company_name.trim().length < 2) {
              errors.company_name =
                '* Company Name must be at least 2 characters';
            }

            if (!values.email) {
              errors.email = '* Required';
            } else if (
              !emailRegex.test(values.email) ||
              !checkEmail(values.email)
            ) {
              errors.email = '* Invalid email address';
            }

            return errors;
          }}
          onSubmit={async (values, { setFieldError }) => {
            dispatch(loading(true));

            try {
              const check = await checkPhone(values.phone_number);
              const isEmailValid = await checkEmail(values.email);

              if (!check.valid) {
                setFieldError(
                  'phone_number',
                  '* Please enter a valid phone number',
                );
                dispatch(loading(false));
              } else if (!isEmailValid) {
                setFieldError('email', '* Invalid email address');
                dispatch(loading(false));
              } else {
                save(values);
              }
            } catch (e) {
              dispatch(handleAsyncError(e));
            }
          }}
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form
              className={classNames({ 'mt-4': session.hasCustomFlag('netx') })}
            >
              <div className="flex flex-col mt-6 mb-4 font-avenir-book">
                <ColumnInput
                  error={errors.first_name}
                  touched={touched.first_name}
                >
                  <StickyInput
                    id="first_name"
                    placeholder="First Name"
                    values={values}
                  />
                </ColumnInput>

                <ColumnInput
                  error={errors.last_name}
                  touched={touched.last_name}
                  className="flex flex-col xxs:flex-row justify-between gap-x-1"
                >
                  <StickyInput
                    className="w-full xxs:w-3/4"
                    id="last_name"
                    placeholder="Last Name"
                    values={values}
                  />

                  <StickySelect
                    className={classNames('w-full xxs:w-1/5 mt-3 xxs:mt-0', {
                      'mb-5': touched.last_name && errors.last_name,
                    })}
                    error={errors.suffix}
                    id="suffix"
                    placeholder="Suffix"
                    options={suffixes}
                    values={values}
                  />
                </ColumnInput>

                <ColumnInput error={errors.email} touched={touched.email}>
                  <StickyInput
                    id="email"
                    type="email"
                    placeholder="Owner Email"
                    values={values}
                  />
                </ColumnInput>

                <ColumnInput
                  error={errors.phone_number}
                  touched={touched.phone_number}
                >
                  <StickyInput
                    id="phone_number"
                    type="tel"
                    mask="(999) 999-9999"
                    placeholder="Mobile Phone"
                    values={values}
                  />
                </ColumnInput>

                <ColumnInput
                  error={errors.company_name}
                  touched={touched.company_name}
                >
                  <StickyInput
                    id="company_name"
                    placeholder="Company Name"
                    values={values}
                  />
                </ColumnInput>

                <ColumnInput>
                  <StickyInput
                    id="dba"
                    placeholder="DBA, if any"
                    values={values}
                  />
                </ColumnInput>

                <div className="text-sm mt-2 mb-4 px-3">
                  By creating an account, I agree to the
                  <a
                    href="https://www.creditkey.com/legal/terms-of-service-and-electronic-disclosures"
                    target="_new"
                    className="text-primary-blue cursor-pointer mx-1"
                  >
                    Credit Key Terms of Service &amp; Electronic Disclosures
                  </a>
                  and
                  <a
                    href="https://www.creditkey.com/legal/privacy-policy"
                    target="_new"
                    className="text-primary-blue cursor-pointer mx-1"
                  >
                    Privacy Policy
                  </a>
                </div>
                <CTAButton
                  buttonClass="w-3/4 mobile:w-2/3 mx-auto md:w-full bg-primary-blue font-avenir-black tracking-wider text-white rounded-full uppercase py-3 px-6 text-sm md:text-base inline-block hover:bg-navy-blue flex justify-center items-center"
                  buttonText="Continue"
                  disabled={isLoading || isRouting}
                  isLoading={isLoading || isRouting}
                />
              </div>
            </Form>
          )}
        </Formik>

        <div className="my-3 font-avenir-medium">
          <Link
            to="/init/existing_user"
            className="cursor-pointer mx-1 text-primary-blue"
          >
            Already have a Credit Key account?
          </Link>
        </div>
      </div>
    );
  }
}
